import { cloneDeep, isEqual } from 'lodash'

import { conditionsTags, resolveConditionTagFromLegacyCondition, validateConditionValue } from '@/utils'

import { ruleTags, ruleTagsByType, ruleTypes } from '@/components/rules/utils'
import { ruleProcessor } from '@/components/services/tariffs/utils'

import render from './render'

const normalizeYuloreValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => {
        return Number.parseInt(item, 10)
      })
      .filter(item => {
        return Number.isFinite(item)
      })
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return [ value ]
  }

  return []
}

const rulesMethodsByType = {
  [ruleTypes.conditions]: {
    _inputFilter(data) {
      const result = {}
      for (const conditionTag of conditionsTags) {
        if (this.whitelist.length) {
          if (!~this.whitelist.indexOf(conditionTag)) {
            continue
          }
        }
        if (this.blacklist.length) {
          if (~this.blacklist.indexOf(conditionTag)) {
            continue
          }
        }
        if (!result[conditionTag]) {
          result[conditionTag] = ruleProcessor(undefined, conditionTag)
        }
      }
      if (Array.isArray(data)) {
        data.reduce((accumulator, { path, type, tag, value }) => {
          const resolvedConditionTag = resolveConditionTagFromLegacyCondition({
            path,
            type,
            tag,
            value
          })
          if (resolvedConditionTag) {
            accumulator[resolvedConditionTag] = ruleProcessor(value, resolvedConditionTag)
          }

          return accumulator
        }, result)
      }

      return result
    },

    _outputFilter(data) {
      const result = []
      for (const tag in data) {
        const rawValue = data[tag]
        let value = rawValue
        if (tag === ruleTags.yulore) {
          value = normalizeYuloreValue(rawValue)
        }
        if (validateConditionValue(tag, value)) {
          result.push({
            tag,
            value
          })
        }
      }

      return result
    }
  },
  [ruleTypes.actions]: {
    _inputFilter(data) {
      const result = cloneDeep(data) || {}
      if (!result[ruleTags.sender]) {
        result[ruleTags.sender] = []
      }

      return result
    },

    _outputFilter(data) {
      const result = cloneDeep(data) || {}
      if (Array.isArray(result[ruleTags.sender]) && !result[ruleTags.sender].length) {
        delete result[ruleTags.sender]
      }

      return result
    }
  }
}

export default {
  name: 'RulesTemplate',

  props: {
    value: null,

    whitelist: {
      type: Array,
      default: () => {
        return []
      }
    },
    blacklist: {
      type: Array,
      default: () => {
        return []
      }
    },

    ruleType: {
      type: String,
      default: ruleTypes.conditions,
      validator: value => {
        return ~Object.values(ruleTypes).indexOf(value)
      }
    }
  },

  data() {
    return {
      proxy: this.value,

      tag: undefined,
      showDialog: false
    }
  },

  computed: {
    items() {
      return ruleTagsByType[this.ruleType].filter(this._ruleFilter)
    }
  },

  watch: {
    value: {
      handler() {
        this.receiveValue()
      },
      deep: true
    },
    proxy: {
      handler() {
        this.transmitValue()
      },
      deep: true
    }
  },

  mounted() {
    this.receiveValue()
  },

  methods: {
    _inputFilter(data) {
      return rulesMethodsByType[this.ruleType]._inputFilter.call(this, data)
    },

    _outputFilter(data) {
      return rulesMethodsByType[this.ruleType]._outputFilter.call(this, data)
    },

    _ruleFilter(tag) {
      if (this.whitelist.length) {
        if (!~this.whitelist.indexOf(tag)) {
          return false
        }
      }
      if (this.blacklist.length) {
        if (~this.blacklist.indexOf(tag)) {
          return false
        }
      }

      return true
    },

    receiveValue() {
      const value = this._inputFilter(this.value)
      if (!isEqual(this.proxy, value)) {
        this.proxy = value
      }
    },
    transmitValue() {
      const proxy = this._outputFilter(this.proxy)
      if (!isEqual(proxy, this.value)) {
        this.$emit('input', proxy)
      }
    }
  },

  render
}
